import React, { useState } from 'react';
import Icon from '../primitives/Icon';
import WarehouseLogo, { WAREHOUSES, warehouseLabel } from '../primitives/WarehouseLogo';
import { cn } from '../../components/ui/cn';
import './chat.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
const API_ORIGIN = API_BASE_URL.replace(/\/api\/?$/, '');

function resolveDownloadUrl(url) {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith('/')) return `${API_ORIGIN}${url}`;
  return url;
}

const Section = ({ title, count, children, defaultOpen = true, action, onAction }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="q-rail__section">
      <div className="q-rail__section-bar">
        <button type="button" className="q-rail__section-head" onClick={() => setOpen(o => !o)}>
          <span className="q-rail__section-label">{title}</span>
          <Icon name={open ? 'chevron-down' : 'chevron-right'} size={14} className="q-rail__chev" />
          {typeof count === 'number' && (
            <span className="q-rail__count">{count}</span>
          )}
        </button>
        {action && (
          <button
            type="button"
            className="q-rail__section-action"
            title={action}
            aria-label={action}
            onClick={(e) => { e.stopPropagation(); onAction && onAction(); }}
          >
            <Icon name="plus" size={16} />
          </button>
        )}
      </div>
      {open && <div className="q-rail__body">{children}</div>}
    </div>
  );
};

const ArtifactRow = ({
  icon, iconColor, warehouse, title, subtitle, status, active, onClick, onAttach, attached,
  rightAdornment,
}) => (
  <div
    className={cn('q-rail__row', active && 'q-rail__row--active', onClick && 'q-rail__row--clickable')}
    onClick={onClick}
  >
    <div className="q-rail__icon" style={iconColor ? { color: iconColor } : undefined}>
      {WAREHOUSES[warehouse] ? <WarehouseLogo type={warehouse} size={16} /> : <Icon name={icon} size={15} />}
    </div>
    <div className="q-rail__text">
      <div className="q-rail__title">{title}</div>
      {subtitle && <div className="q-rail__sub">{subtitle}</div>}
    </div>
    {status === 'training' && <div className="q-spin" title="Working" />}
    {rightAdornment}
    {status === 'ready' && onAttach && (
      <button
        type="button"
        className={cn('q-rail__attach', attached && 'q-rail__attach--on')}
        onClick={(e) => { e.stopPropagation(); onAttach(); }}
        title={attached ? 'Attached' : 'Attach to chat'}
      >
        <Icon name={attached ? 'check' : 'paperclip'} size={13} />
      </button>
    )}
  </div>
);

const PipelinePivot = ({ hasRaw, hasPreprocessed, onOpenRaw, onOpenPreprocessed }) => {
  if (!hasRaw && !hasPreprocessed) return null;
  const Btn = ({ disabled, onClick, title, icon }) => (
    <button
      type="button"
      className="q-rail__mini"
      onClick={(e) => { e.stopPropagation(); if (!disabled) onClick && onClick(); }}
      disabled={disabled}
      title={title}
    >
      <Icon name={icon} size={12} />
    </button>
  );
  return (
    <div className="q-rail__pivot">
      <Btn icon="file-text" disabled={!hasRaw} onClick={onOpenRaw} title="View raw upload" />
      <Icon name="arrow-right" size={9} />
      <Btn icon="sparkles" disabled={!hasPreprocessed} onClick={onOpenPreprocessed} title="View preprocessed" />
    </div>
  );
};

const STATUS = {
  ok: { label: 'Connected' },
  error: { label: 'Connection error', cls: 'error' },
  unverified: { label: 'Not verified', cls: 'idle' },
};

// A connection keeps its user-given name; the auto-generated "Bigquery
// connection" default reads better as the warehouse's own name.
const connectionTitle = (c) => (
  !c.name || c.name.toLowerCase() === `${c.connection_type} connection`
    ? warehouseLabel(c.connection_type)
    : c.name
);

const SourceRow = ({ connection, tablesHere, onOpen }) => {
  const st = STATUS[connection.status] || STATUS.unverified;
  const usage = tablesHere > 0
    ? `${tablesHere} table${tablesHere === 1 ? '' : 's'} in this analysis`
    : st.label;
  return (
    <ArtifactRow
      icon="database"
      warehouse={connection.connection_type}
      title={connectionTitle(connection)}
      subtitle={connection.status === 'error' ? st.label : usage}
      onClick={onOpen ? () => onOpen(connection) : null}
      // Healthy connections stay quiet; only a problem earns a dot.
      rightAdornment={connection.status !== 'ok' && (
        <span className={cn('q-rail__status', `q-rail__status--${st.cls}`)} title={st.label} />
      )}
    />
  );
};

// No warehouse yet: one quiet row with the four marks stacked, so the rail
// says what can be connected without a list of empty slots.
const ConnectSourceRow = ({ onClick }) => (
  <button type="button" className="q-rail__connect" onClick={onClick}>
    <span className="q-rail__logo-stack" aria-hidden>
      {Object.keys(WAREHOUSES).map(k => (
        <span key={k} className="q-rail__logo-chip"><WarehouseLogo type={k} size={11} /></span>
      ))}
    </span>
    <span>Connect a warehouse</span>
  </button>
);

const NavItem = ({ icon, label, title, active, disabled, onClick }) => (
  <button
    type="button"
    className={cn('q-rail__nav-item', active && 'q-rail__nav-item--active')}
    onClick={onClick}
    disabled={disabled}
    title={title}
  >
    <Icon name={icon} size={18} />
    <span>{label}</span>
  </button>
);

const ThreadRow = ({ title, when, active, onClick }) => (
  <div
    className={cn('q-rail__thread', active && 'q-rail__thread--active')}
    onClick={onClick}
  >
    <div className="q-rail__title">{title}</div>
    {when && <div className="q-rail__sub">{when}</div>}
  </div>
);

const ArtifactsRail = ({
  datasets = [], models = [], predictions = [], threads = [],
  activeThread, onSelectThread, onNewChat, newChatActive = false,
  attachedIds = [], onAttach,
  connections = [], onConnectSource,
  userInitials = '', userName = '', userPlan = '',
  onSettingsClick,
  onOpenDataset,
  activeDatasetId,
  onNavigateBack,
  onExportNotebook,
  notebookExporting = false,
  onManagePipeline,
}) => (
  <aside className="q-rail">
    <div className="q-rail__brand">
      {onNavigateBack ? (
        <button type="button" className="q-rail__word" onClick={onNavigateBack} title="Back to overview">
          Qombra
        </button>
      ) : (
        <div className="q-rail__word">Qombra</div>
      )}
      {onNavigateBack && (
        <button
          type="button"
          className="q-rail__iconbtn"
          onClick={onNavigateBack}
          title="All analyses"
          aria-label="All analyses"
        >
          <Icon name="layout-grid" size={18} />
        </button>
      )}
    </div>

    <nav className="q-rail__nav">
      {onNewChat && (
        <NavItem icon="square-pen" label="New analysis" active={newChatActive} onClick={onNewChat} />
      )}
      {onManagePipeline && (
        <NavItem
          icon="workflow"
          label="Manage pipeline"
          title="Schedule recurring runs, re-training and prediction writeback"
          onClick={(e) => onManagePipeline(e.currentTarget)}
        />
      )}
      {onExportNotebook && (
        <NavItem
          icon={notebookExporting ? 'loader-2' : 'notebook'}
          label={notebookExporting ? 'Exporting…' : 'Export notebook'}
          title="Export the analysis as a Jupyter notebook"
          disabled={notebookExporting}
          onClick={notebookExporting ? undefined : onExportNotebook}
        />
      )}
    </nav>

    <div className="q-rail__scroll">
      {(onConnectSource || connections.length > 0) && (
        <Section
          title="Sources"
          count={connections.length || undefined}
          action={onConnectSource && connections.length > 0 ? 'Connect a warehouse' : undefined}
          onAction={() => onConnectSource()}
        >
          {connections.map(c => (
            <SourceRow
              key={c.id}
              connection={c}
              tablesHere={datasets.filter(d => d.source?.connectionId === c.id).length}
              onOpen={onConnectSource}
            />
          ))}
          {connections.length === 0 && <ConnectSourceRow onClick={() => onConnectSource()} />}
        </Section>
      )}

      <Section title="Datasets" count={datasets.length}>
        {datasets.length === 0 && (
          <div className="q-rail__empty">
            Import a table from a source, or drop a file into the chat.
          </div>
        )}
        {datasets.map(d => {
          const hasRaw = Array.isArray(d.rawUploads) && d.rawUploads.length > 0;
          const hasPre = !!d.preprocessedId;
          const handleOpen = (initialView) => {
            if (!onOpenDataset) return;
            onOpenDataset({ groupId: d.id, initialView });
          };
          return (
            <ArtifactRow
              key={d.id}
              icon="file-spreadsheet"
              warehouse={d.source?.type}
              title={d.name}
              subtitle={d.subtitle}
              status="ready"
              active={activeDatasetId === d.id}
              attached={attachedIds.includes(d.id)}
              onAttach={onAttach ? () => onAttach(d.id) : null}
              onClick={onOpenDataset ? () => handleOpen(hasPre ? 'preprocessed' : 'raw') : null}
              rightAdornment={(hasRaw || hasPre) && (
                <PipelinePivot
                  hasRaw={hasRaw}
                  hasPreprocessed={hasPre}
                  onOpenRaw={() => handleOpen('raw')}
                  onOpenPreprocessed={() => handleOpen('preprocessed')}
                />
              )}
            />
          );
        })}
      </Section>

      <Section title="Models" count={models.length}>
        {models.length === 0 ? (
          <div className="q-rail__empty">
            No models yet. Ask the agent to train one.
          </div>
        ) : models.map(m => (
          <ArtifactRow
            key={m.id}
            icon="brain"
            iconColor={m.status === 'training' ? 'var(--foreground)' : 'var(--green)'}
            title={m.name}
            subtitle={m.subtitle}
            status={m.status}
            attached={attachedIds.includes(m.id)}
            onAttach={onAttach ? () => onAttach(m.id) : null}
          />
        ))}
      </Section>

      <Section title="Predictions" count={predictions.length}>
        {predictions.length === 0 ? (
          <div className="q-rail__empty">
            Predictions appear here once a model runs.
          </div>
        ) : predictions.map(p => {
          const dlUrl = resolveDownloadUrl(p.downloadUrl);
          return (
            <ArtifactRow
              key={p.id}
              icon="line-chart"
              iconColor="var(--green)"
              title={p.name}
              subtitle={p.subtitle}
              status="ready"
              attached={attachedIds.includes(p.id)}
              onAttach={onAttach ? () => onAttach(p.id) : null}
              rightAdornment={dlUrl && (
                <a
                  href={dlUrl}
                  download
                  className="q-rail__download"
                  onClick={(e) => e.stopPropagation()}
                  title="Download Excel"
                >
                  <Icon name="download" size={13} />
                </a>
              )}
            />
          );
        })}
      </Section>

      {threads.length > 0 && (
        <Section title="Recent analyses" count={threads.length} defaultOpen={false}>
          {threads.map(t => (
            <ThreadRow
              key={t.id}
              title={t.title}
              when={t.when}
              active={activeThread === t.id}
              onClick={() => onSelectThread && onSelectThread(t.id)}
            />
          ))}
        </Section>
      )}
    </div>

    {/* {(userName || userInitials) && (
      <div style={{ padding: '14px 14px 16px', borderTop: '1px solid var(--line-soft)', display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{
          width: 38, height: 38, borderRadius: '50%',
          background: 'var(--accent-soft-2)', color: 'var(--accent)',
          display: 'grid', placeItems: 'center', fontSize: 13, fontWeight: 500,
        }}>{userInitials}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          {userName && <div style={{ fontSize: 13, color: 'var(--fg)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{userName}</div>}
          {userPlan && <div style={{ fontSize: 11, color: 'var(--fg-subtle)' }}>{userPlan}</div>}
        </div>
        {onSettingsClick && (
          <button
            onClick={onSettingsClick}
            title="Settings"
            style={{ background: 'transparent', border: 'none', color: 'var(--fg-subtle)', cursor: 'pointer', padding: 6, display: 'inline-flex' }}
          >
            <Icon name="settings" size={14} />
          </button>
        )}
      </div>
    )} */}
  </aside>
);

export default ArtifactsRail;
